import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { invalidateAll } from '../../api/queryClient';
import { trpc, type RouterInputs, type RouterOutputs } from '../../api/trpc';

export type CatalogInput = RouterInputs['leads']['catalog']['campaigns'];
export type CatalogList = RouterOutputs['leads']['catalog']['campaigns'];
export type CatalogRow = CatalogList['rows'][number];
export type CatalogScope = NonNullable<CatalogInput['scope']>;
export type MarketCode = NonNullable<CatalogInput['market']>;
export type RuleView = NonNullable<CatalogRow['rule']>;
export type RuleSaveInput = RouterInputs['leads']['rules']['save'];
export type Owner = RouterOutputs['leads']['owners']['list'][number];
export type Pipeline = RouterOutputs['leads']['pipelines']['list'][number];
export type FieldOption = RouterOutputs['leads']['options']['campaign'][number];
export type MarketDefault = RouterOutputs['leads']['defaults']['list'][number];
export type LeadSettings = RouterOutputs['leads']['settings']['get'];
export type SettingsSaveInput = RouterInputs['leads']['settings']['save'];
export type Channel = RouterOutputs['leads']['channels']['list'][number];
export type ChannelMode = Channel['mode'];
export type MetaSubscriptionRow = RouterOutputs['leads']['channels']['checkMetaSubscription'][number];
export type SendTestResult = RouterOutputs['leads']['channels']['sendTest'];

/** Ad campaigns with their rule and 30-day lead counts; unruled campaigns with leads come first. */
export function useCatalog(input: CatalogInput, enabled = true) {
  return useQuery(trpc.leads.catalog.campaigns.queryOptions(input, { staleTime: 10_000, enabled }));
}

/** Active Pipedrive users (the owners a rule may name), by name. */
export function useOwners() {
  return useQuery(trpc.leads.owners.list.queryOptions(undefined, { staleTime: 5 * 60_000 }));
}

/** Active pipelines with their active stages, in Pipedrive order. */
export function usePipelines() {
  return useQuery(trpc.leads.pipelines.list.queryOptions(undefined, { staleTime: 5 * 60_000 }));
}

/** Options of the deal field "Kampaň" (server-side search, at most 50). */
export function useCampaignOptions(q: string, enabled: boolean) {
  return useQuery(trpc.leads.options.campaign.queryOptions({ q }, { enabled, staleTime: 60_000 }));
}

/** Options of the deal field "Poptaný projekt" (server-side search, at most 50). */
export function useProjectOptions(q: string, enabled: boolean) {
  return useQuery(trpc.leads.options.project.queryOptions({ q }, { enabled, staleTime: 60_000 }));
}

/** The two market defaults (CZ, ES) in market order: the same shape as a rule. */
export function useMarketDefaults() {
  return useQuery(trpc.leads.defaults.list.queryOptions(undefined, { staleTime: 10_000 }));
}

/** The router's global switches (one versioned row). */
export function useLeadSettings() {
  return useQuery(trpc.leads.settings.get.queryOptions(undefined, { staleTime: 10_000 }));
}

/**
 * Every inbound channel with its URL and live status (web first, then Meta). Refreshed every 30 s
 * while the tab is open, so "last event" moves right after a form is submitted somewhere.
 */
export function useChannels() {
  return useQuery(trpc.leads.channels.list.queryOptions(undefined, { staleTime: 10_000, refetchInterval: 30_000 }));
}

/** The Meta ad accounts with their market and campaign counts (card "Ad accounts (Meta)"). */
export type AdAccount = RouterOutputs['leads']['accounts']['list'][number];
export function useAdAccounts() {
  return useQuery(trpc.leads.accounts.list.queryOptions(undefined, { staleTime: 10_000 }));
}

/** Everything the lead router screens read; a rule save can also add a Kampaň option. */
export function invalidateLeads(): Promise<void> {
  return invalidateAll(trpc.leads.pathFilter(), trpc.performance.mapping.pathFilter());
}

// --- tab "Received leads" ---

export type ReceivedListInput = RouterInputs['leads']['received']['list'];
export type ReceivedList = RouterOutputs['leads']['received']['list'];
export type ReceivedLead = ReceivedList['rows'][number];
export type ReceivedDetail = RouterOutputs['leads']['received']['get'];
export type DeliveryRun = ReceivedDetail['runs'][number];
export type LeadDecision = NonNullable<ReceivedDetail['decision']>;
export type TraceStep = LeadDecision['trace'][number];
export type DryRunInput = RouterInputs['leads']['rules']['dryRun'];
export type DryRunResult = RouterOutputs['leads']['rules']['dryRun'];

/**
 * Received leads, exceptions first, paged by the API's cursor (200 a page). Refreshed every minute while
 * the tab is open: the morning check should not need a reload.
 */
export function useReceivedLeads(input: Omit<ReceivedListInput, 'cursor'>) {
  return useInfiniteQuery(
    trpc.leads.received.list.infiniteQueryOptions(input, {
      getNextPageParam: (last) => last.nextCursor ?? undefined,
      staleTime: 10_000,
      refetchInterval: 60_000,
    }),
  );
}

/** One lead with its trace and delivery runs; polls every 5 s while the lead is still being routed or delivered. */
export function useReceivedLead(id: string | null, inFlight: (status: string) => boolean) {
  return useQuery(
    trpc.leads.received.get.queryOptions(
      { id: id ?? '' },
      {
        enabled: id !== null,
        refetchInterval: (query) => (query.state.data && inFlight(query.state.data.status) ? 5_000 : false),
      },
    ),
  );
}

/** The channels for filters and selects (no polling, unlike `useChannels`). */
export function useChannelOptions() {
  return useQuery(trpc.leads.channels.list.queryOptions(undefined, { staleTime: 5 * 60_000 }));
}
